import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { TodoList } from '@/types/admin/todoLists';
import {
  CADENCE_LABEL,
  EMPTY_LABEL,
  ToDosScopeToggle,
  TodoListRow,
  groupByCadence,
} from '../mobile/ToDosList';
import type { TodoScope } from '../mobile/ToDosList';

interface Props {
  lists: TodoList[];
  isLoading: boolean;
  scope: TodoScope;
  onScopeChange: (scope: TodoScope) => void;
  onSelectList?: (listId: number) => void;
  onCreateList?: () => void;
  onEditList?: (listId: number) => void;
}

/**
 * Desktop To Do's index. DesktopAdminLayout is a fixed windowHeight box on
 * web, so the cadence-grouped content is wrapped in a vertical ScrollView or it
 * would clip off-screen (mirrors DesktopSurveyResponsesView).
 */
export const DesktopToDosView: React.FC<Props> = ({
  lists,
  isLoading,
  scope,
  onScopeChange,
  onSelectList,
  onCreateList,
  onEditList,
}) => {
  const { redesign } = useAdminColors();
  const groups = useMemo(() => groupByCadence(lists), [lists]);

  const subtitle = isLoading
    ? 'Loading to-do lists…'
    : `${lists.length} to-do list${lists.length === 1 ? '' : 's'}`;

  return (
    <DesktopAdminLayout>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <DesktopPageHeader
          title="To Do's"
          subtitle={subtitle}
          showAddButton={onCreateList != null}
          onAddPress={onCreateList}
          addButtonText="New to-do list"
        />

        <ToDosScopeToggle scope={scope} onChange={onScopeChange} />

        {isLoading && lists.length === 0 ? (
          <View style={styles.centered}>
            <ActivityIndicator color={redesign.text.secondary} />
          </View>
        ) : lists.length === 0 ? (
          <View style={styles.centered}>
            <Text style={[styles.emptyText, { color: redesign.text.muted }]}>
              {EMPTY_LABEL}
            </Text>
          </View>
        ) : (
          groups.map(group => (
            <View key={group.cadence} style={styles.section}>
              <Text
                style={[styles.sectionHeader, { color: redesign.text.subtle }]}
              >
                {CADENCE_LABEL[group.cadence]}
              </Text>
              {group.lists.map(list => (
                <TodoListRow
                  key={list.id}
                  list={list}
                  onPress={onSelectList}
                  onEdit={onEditList}
                />
              ))}
            </View>
          ))
        )}
      </ScrollView>
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 24, gap: 20 },
  centered: {
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyText: { fontSize: 15, textAlign: 'center' },
  section: { gap: 10 },
  sectionHeader: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
});
